import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import ReadOnlyPythonCode from './ReadOnlyPythonCode';
import api from '../api/client';

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

const VERDICT_LABELS = {
  ACCEPTED: 'Принято',
  REVIEW_IN_LESSON: 'Посмотрим на занятии',
  REDO: 'Переделать',
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return '—';
  }
}

function hasWorkspaceState(ws) {
  if (!ws || typeof ws !== 'object') return false;
  return Object.keys(ws).length > 0;
}

function studentLabel(group) {
  return group?.student_display_name || group?.student_username || `Ученик #${group?.student_id}`;
}

function attemptCode(attempt) {
  return String(attempt?.code_snapshot ?? '').trim();
}

function attemptTextAnswer(attempt) {
  return String(attempt?.tests_result?.student_text_answer ?? '').trim();
}

function draftHasContent(draft) {
  if (!draft) return false;
  if (String(draft.code ?? '').trim()) return true;
  if (String(draft.student_answer ?? '').trim()) return true;
  return Boolean(draft.has_workspace);
}

/**
 * Деталь одной попытки или черновика (справа).
 */
function AttemptDetail({ item }) {
  if (!item) {
    return <p className="text-secondary">Выберите попытку слева.</p>;
  }

  if (item.kind === 'draft') {
    const code = String(item.code ?? '').trim();
    const answer = String(item.student_answer ?? '').trim();
    return (
      <>
        <div className="task-attempts-history-detail-head">
          <span className="badge">Черновик</span>
          <span className="text-secondary">{fmtDateTime(item.updated_at)}</span>
        </div>
        {code ? (
          <div className="task-attempts-history-block">
            <strong>Код</strong>
            <ReadOnlyPythonCode value={code} className="task-attempts-history-code" />
          </div>
        ) : answer ? (
          <div className="task-attempts-history-block">
            <strong>Ответ</strong>
            <div className="text-secondary task-attempts-history-pre">{answer}</div>
          </div>
        ) : item.has_workspace ? (
          <p className="text-secondary">Есть чертёж или таблица (без кода).</p>
        ) : (
          <p className="text-secondary">Пустой черновик.</p>
        )}
      </>
    );
  }

  const code = attemptCode(item);
  const textAnswer = attemptTextAnswer(item);
  const hasBoard = hasWorkspaceState(item.workspace_state);

  return (
    <>
      <div className="task-attempts-history-detail-head">
        <span className="badge">
          {SELF_REPORT_LABELS[item.self_report_status] || item.self_report_status || '!'}
        </span>
        <span className="text-secondary">{fmtDateTime(item.created_at)}</span>
        {item.teacher_verdict ? (
          <span className="badge">
            {VERDICT_LABELS[item.teacher_verdict] || item.teacher_verdict}
          </span>
        ) : null}
        {item.saved_by_teacher ? (
          <span className="text-secondary">сохранил преподаватель</span>
        ) : null}
      </div>

      {item.self_comment ? (
        <div className="task-attempts-history-block">
          <strong>Отчёт / комментарий</strong>
          <div className="text-secondary task-attempts-history-pre">{item.self_comment}</div>
        </div>
      ) : null}

      {item.teacher_comment ? (
        <div className="task-attempts-history-block">
          <strong>Комментарий преподавателя</strong>
          <div className="text-secondary task-attempts-history-pre">{item.teacher_comment}</div>
        </div>
      ) : null}

      {code ? (
        <div className="task-attempts-history-block">
          <strong>Код</strong>
          <ReadOnlyPythonCode value={code} className="task-attempts-history-code" />
        </div>
      ) : textAnswer ? (
        <div className="task-attempts-history-block">
          <strong>Ответ</strong>
          <div className="text-secondary task-attempts-history-pre">{textAnswer}</div>
        </div>
      ) : hasBoard ? (
        <p className="text-secondary">Сохранено состояние доски.</p>
      ) : (
        <p className="text-secondary">В этой попытке нет кода и ответа.</p>
      )}
    </>
  );
}

function AttemptListRow({ attempt, active, onSelect }) {
  const label = SELF_REPORT_LABELS[attempt.self_report_status] || attempt.self_report_status || '!';
  const verdict = attempt.teacher_verdict
    ? VERDICT_LABELS[attempt.teacher_verdict] || attempt.teacher_verdict
    : '';
  return (
    <button
      type="button"
      className={`task-attempts-history-row${active ? ' active' : ''}`}
      onClick={onSelect}
    >
      <div className="task-attempts-history-row-top">
        <span className="text-secondary">{fmtDateTime(attempt.created_at)}</span>
        <span className="task-attempts-history-report">{label}</span>
      </div>
      {(verdict || attempt.saved_by_teacher) && (
        <div className="task-attempts-history-row-meta">
          {verdict ? <span>{verdict}</span> : null}
          {attempt.saved_by_teacher ? (
            <span className="text-secondary">сохранил преподаватель</span>
          ) : null}
        </div>
      )}
    </button>
  );
}

/**
 * Просмотр попыток учеников по задаче (преподаватель).
 *
 * mode:
 * - "student" — попытки одного ученика (studentId обязателен)
 * - "picker" — сначала список учеников с попытками, затем их история
 * - "all" — все попытки по ученикам; черновик, если попыток нет
 */
export default function TeacherTaskAttemptsModal({
  open,
  onClose,
  mode = 'student',
  taskId,
  taskSlug,
  studentId = null,
  studentName = '',
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState([]);
  const [taskAttemptsData, setTaskAttemptsData] = useState(null);
  const [pickerStudentId, setPickerStudentId] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const effectiveStudentId = mode === 'picker' ? pickerStudentId : studentId;

  // До paint: иначе один кадр с «попыток нет» при loading=false со прошлого закрытия.
  useLayoutEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setAttempts([]);
    setTaskAttemptsData(null);
    setPickerStudentId(null);
    setSelectedKey(null);
  }, [open, mode, taskId, taskSlug, studentId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    (async () => {
      try {
        if (mode === 'student' && studentId) {
          const res = await api.get(`/teacher/students/${studentId}/attempts/`, {
            params: { task: taskSlug || String(taskId) },
          });
          if (cancelled) return;
          const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
          setAttempts(list);
          if (list.length) setSelectedKey(`a:${list[0].id}`);
        } else if ((mode === 'picker' || mode === 'all') && taskId) {
          const res = await api.get(`/teacher/tasks/${taskId}/attempts/`);
          if (cancelled) return;
          setTaskAttemptsData(res.data || null);
        } else {
          if (cancelled) return;
          setError('Недостаточно данных для загрузки попыток.');
        }
      } catch (e) {
        if (cancelled) return;
        const detail = e.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Не удалось загрузить попытки.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, mode, taskId, taskSlug, studentId]);

  const groups = useMemo(
    () => (Array.isArray(taskAttemptsData?.students) ? taskAttemptsData.students : []),
    [taskAttemptsData],
  );

  const draftsByStudent = useMemo(() => {
    const map = new Map();
    for (const d of taskAttemptsData?.drafts || []) {
      if (d?.student_id != null && draftHasContent(d)) {
        map.set(d.student_id, d);
      }
    }
    return map;
  }, [taskAttemptsData]);

  const pickerGroup = useMemo(() => {
    if (mode !== 'picker' || !pickerStudentId) return null;
    return groups.find((g) => g.student_id === pickerStudentId) || null;
  }, [mode, pickerStudentId, groups]);

  const studentAttempts = useMemo(() => {
    if (mode === 'student') return attempts;
    if (mode === 'picker' && pickerGroup) {
      return Array.isArray(pickerGroup.attempts) ? pickerGroup.attempts : [];
    }
    return [];
  }, [mode, attempts, pickerGroup]);

  useEffect(() => {
    if (!open || mode !== 'picker' || !pickerStudentId) return;
    const list = Array.isArray(pickerGroup?.attempts) ? pickerGroup.attempts : [];
    if (list.length) {
      setSelectedKey(`a:${list[0].id}`);
    } else {
      const draft = draftsByStudent.get(pickerStudentId);
      setSelectedKey(draft ? `d:${pickerStudentId}` : null);
    }
  }, [open, mode, pickerStudentId, pickerGroup, draftsByStudent]);

  const allRows = useMemo(() => {
    if (mode !== 'all') return [];
    const rows = [];
    const seen = new Set();
    for (const group of groups) {
      const sid = group.student_id;
      seen.add(sid);
      const list = Array.isArray(group.attempts) ? group.attempts : [];
      if (list.length) {
        for (const a of list) {
          rows.push({
            key: `a:${a.id}`,
            studentId: sid,
            label: studentLabel(group),
            kind: 'attempt',
            item: a,
          });
        }
      } else {
        const draft = draftsByStudent.get(sid);
        if (draft) {
          rows.push({
            key: `d:${sid}`,
            studentId: sid,
            label: studentLabel(group),
            kind: 'draft',
            item: { kind: 'draft', ...draft },
          });
        }
      }
    }
    for (const [sid, draft] of draftsByStudent.entries()) {
      if (seen.has(sid)) continue;
      rows.push({
        key: `d:${sid}`,
        studentId: sid,
        label: `Ученик #${sid}`,
        kind: 'draft',
        item: { kind: 'draft', ...draft },
      });
    }
    return rows;
  }, [mode, groups, draftsByStudent]);

  useEffect(() => {
    if (!open || mode !== 'all') return;
    if (allRows.length && !selectedKey) {
      setSelectedKey(allRows[0].key);
    }
  }, [open, mode, allRows, selectedKey]);

  const selectedItem = useMemo(() => {
    if (!selectedKey) return null;
    if (mode === 'all') {
      const row = allRows.find((r) => r.key === selectedKey);
      return row?.item || null;
    }
    if (selectedKey.startsWith('d:')) {
      const sid = Number(selectedKey.slice(2));
      const draft = draftsByStudent.get(sid);
      return draft ? { kind: 'draft', ...draft } : null;
    }
    if (selectedKey.startsWith('a:')) {
      const id = Number(selectedKey.slice(2));
      return studentAttempts.find((a) => a.id === id) || null;
    }
    return null;
  }, [selectedKey, mode, allRows, studentAttempts, draftsByStudent]);

  const title = useMemo(() => {
    if (mode === 'all') return 'Все попытки по задаче';
    if (mode === 'picker' && !pickerStudentId) return 'Ученики с попытками';
    const name = studentName
      || (pickerGroup ? studentLabel(pickerGroup) : '')
      || (effectiveStudentId ? `Ученик #${effectiveStudentId}` : '');
    return name ? `Попытки: ${name}` : 'Попытки ученика';
  }, [mode, pickerStudentId, studentName, pickerGroup, effectiveStudentId]);

  const showPickerList = mode === 'picker' && !pickerStudentId;
  const showStudentHistory = mode === 'student' || (mode === 'picker' && pickerStudentId);
  const truncated = Boolean(taskAttemptsData?.truncated);

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div className="task-attempts-history">
        {loading ? (
          <p className="text-secondary">Загрузка…</p>
        ) : error ? (
          <div className="alert alert-error">{error}</div>
        ) : showPickerList ? (
          groups.length === 0 && draftsByStudent.size === 0 ? (
            <div className="task-attempts-history-empty">
              <p>По этой задаче попыток учеников ещё нет.</p>
            </div>
          ) : (
            <div className="task-attempts-history-list teacher-task-attempts-picker">
              {groups.map((group) => {
                const count = Array.isArray(group.attempts) ? group.attempts.length : 0;
                const draftOnly = count === 0 && draftsByStudent.has(group.student_id);
                return (
                  <button
                    key={group.student_id}
                    type="button"
                    className="task-attempts-history-row"
                    onClick={() => {
                      setPickerStudentId(group.student_id);
                      const list = Array.isArray(group.attempts) ? group.attempts : [];
                      if (list.length) {
                        setSelectedKey(`a:${list[0].id}`);
                      } else if (draftsByStudent.has(group.student_id)) {
                        setSelectedKey(`d:${group.student_id}`);
                      } else {
                        setSelectedKey(null);
                      }
                    }}
                  >
                    <div className="task-attempts-history-row-top">
                      <span>{studentLabel(group)}</span>
                      <span className="text-secondary">
                        {draftOnly ? 'черновик' : count}
                      </span>
                    </div>
                  </button>
                );
              })}
              {[...draftsByStudent.entries()]
                .filter(([sid]) => !groups.some((g) => g.student_id === sid))
                .map(([sid, draft]) => (
                  <button
                    key={`draft-only-${sid}`}
                    type="button"
                    className="task-attempts-history-row"
                    onClick={() => {
                      setPickerStudentId(sid);
                      setSelectedKey(`d:${sid}`);
                    }}
                  >
                    <div className="task-attempts-history-row-top">
                      <span>Ученик #{sid}</span>
                      <span className="text-secondary">черновик</span>
                    </div>
                    <div className="task-attempts-history-row-meta">
                      <span className="text-secondary">{fmtDateTime(draft.updated_at)}</span>
                    </div>
                  </button>
                ))}
              {truncated ? (
                <p className="text-secondary" style={{ padding: '0.5rem' }}>
                  Показаны не все попытки (лимит).
                </p>
              ) : null}
            </div>
          )
        ) : showStudentHistory ? (
          <>
            {mode === 'picker' ? (
              <div className="teacher-task-attempts-back">
                <button
                  type="button"
                  className="btn btn-outline btn-xs"
                  onClick={() => {
                    setPickerStudentId(null);
                    setSelectedKey(null);
                  }}
                >
                  ← К списку учеников
                </button>
              </div>
            ) : null}
            {studentAttempts.length === 0
            && !(mode === 'picker' && draftsByStudent.has(pickerStudentId)) ? (
              <div className="task-attempts-history-empty">
                <p>Сохранённых попыток у этого ученика ещё нет.</p>
              </div>
            ) : (
              <div className="task-attempts-history-split">
                <aside className="task-attempts-history-list" aria-label="Список попыток">
                  {studentAttempts.map((a) => (
                    <AttemptListRow
                      key={a.id}
                      attempt={a}
                      active={selectedKey === `a:${a.id}`}
                      onSelect={() => setSelectedKey(`a:${a.id}`)}
                    />
                  ))}
                  {mode === 'picker' && draftsByStudent.has(pickerStudentId) && studentAttempts.length === 0 ? (
                    <button
                      type="button"
                      className={`task-attempts-history-row${selectedKey === `d:${pickerStudentId}` ? ' active' : ''}`}
                      onClick={() => setSelectedKey(`d:${pickerStudentId}`)}
                    >
                      <div className="task-attempts-history-row-top">
                        <span className="text-secondary">
                          {fmtDateTime(draftsByStudent.get(pickerStudentId)?.updated_at)}
                        </span>
                        <span className="task-attempts-history-report">черновик</span>
                      </div>
                    </button>
                  ) : null}
                </aside>
                <main className="task-attempts-history-detail">
                  <AttemptDetail item={selectedItem} />
                </main>
              </div>
            )}
          </>
        ) : mode === 'all' ? (
          allRows.length === 0 ? (
            <div className="task-attempts-history-empty">
              <p>Попыток и черновиков пока нет.</p>
            </div>
          ) : (
            <div className="task-attempts-history-split">
              <aside className="task-attempts-history-list" aria-label="Попытки всех учеников">
                {allRows.map((row) => {
                  const active = selectedKey === row.key;
                  if (row.kind === 'draft') {
                    return (
                      <button
                        key={row.key}
                        type="button"
                        className={`task-attempts-history-row${active ? ' active' : ''}`}
                        onClick={() => setSelectedKey(row.key)}
                      >
                        <div className="task-attempts-history-row-top">
                          <span>{row.label}</span>
                          <span className="task-attempts-history-report">черновик</span>
                        </div>
                        <div className="task-attempts-history-row-meta">
                          <span className="text-secondary">{fmtDateTime(row.item.updated_at)}</span>
                        </div>
                      </button>
                    );
                  }
                  const a = row.item;
                  const label = SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '!';
                  return (
                    <button
                      key={row.key}
                      type="button"
                      className={`task-attempts-history-row${active ? ' active' : ''}`}
                      onClick={() => setSelectedKey(row.key)}
                    >
                      <div className="task-attempts-history-row-top">
                        <span>{row.label}</span>
                        <span className="task-attempts-history-report">{label}</span>
                      </div>
                      <div className="task-attempts-history-row-meta">
                        <span className="text-secondary">{fmtDateTime(a.created_at)}</span>
                      </div>
                    </button>
                  );
                })}
                {truncated ? (
                  <p className="text-secondary" style={{ padding: '0.5rem' }}>
                    Показаны не все попытки (лимит {taskAttemptsData?.count}).
                  </p>
                ) : null}
              </aside>
              <main className="task-attempts-history-detail">
                <AttemptDetail item={selectedItem} />
              </main>
            </div>
          )
        ) : null}
      </div>
    </Modal>
  );
}
